import { cn } from '@/lib/utils';
import { PaperclipIcon } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Where files are handed to a task: dropped on the zone, or picked with
 * "Add files" (spec 120 FR-001, FR-007).
 *
 * Files only. A dropped folder is not walked — an attachment is one file in
 * one folder — and the zone says so rather than silently taking nothing.
 * The drag counter is the Files page's (`useFileUploads`): enter and leave
 * fire for every child the pointer crosses, so a boolean would flicker.
 */
export function AttachmentPicker({
  onFiles,
  disabled = false,
  label = 'Add files',
  compact = false,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
  /**
   * A plain row rather than a drop zone, outlined only while files are dragged
   * over it (spec 181): under files already attached, a large dashed area
   * outweighed the files themselves.
   */
  compact?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (disabled) return;
    const entries = Array.from(event.dataTransfer.items);
    const hasFolder = entries.some(
      (item) => item.webkitGetAsEntry?.()?.isDirectory === true,
    );
    const files = Array.from(event.dataTransfer.files).filter(
      (_file, index) => {
        const entry = entries[index]?.webkitGetAsEntry?.();
        return entry ? !entry.isDirectory : true;
      },
    );
    setNote(
      hasFolder
        ? 'Folders cannot be attached — add the files inside them.'
        : null,
    );
    if (files.length > 0) onFiles(files);
  };

  return (
    <div
      data-testid="attachment-drop-zone"
      data-dragging={dragging || undefined}
      onDragEnter={(event) => {
        event.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      }}
      onDrop={onDrop}
      className={
        compact
          ? 'data-[dragging]:border-primary data-[dragging]:bg-accent/40 flex flex-col items-start gap-2 rounded-md border border-dashed border-transparent data-[dragging]:p-3'
          : // On a phone, which drops no files, just the button, full width
            // (spec 270): the drop box around it framed an empty promise.
            'border-border data-[dragging]:border-primary data-[dragging]:bg-accent/40 flex flex-col items-start gap-2 rounded-md border border-dashed p-3 max-md:items-stretch max-md:border-0 max-md:p-0'
      }
    >
      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          !compact && 'max-md:w-full',
        )}
      >
        <Button
          type="button"
          variant={compact ? 'ghost' : 'outline'}
          size="sm"
          className={compact ? undefined : 'max-md:w-full'}
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          <PaperclipIcon aria-hidden="true" className="size-4" />
          {label}
        </Button>
        <span className="text-muted-foreground text-xs pointer-coarse:hidden">
          or drop files here
        </span>
      </div>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label={label}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
      {note ? <p className="text-muted-foreground text-xs">{note}</p> : null}
    </div>
  );
}
